import React, {useState, useRef, useEffect} from 'react';
import {
  View,
  StyleSheet,
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  TouchableOpacity,
  TextInput,
} from 'react-native';
import {Text} from 'react-native-paper';
import {useTranslation} from 'react-i18next';
import {onboardingColors} from '../../theme/onboardingColors';
import {useRegistrationStore} from '../../store/useRegistrationStore';
import {useAuthStore} from '../../store/useAuthStore';
import {authApi} from '../../services/api/authApi';
import StepIndicator from '../../components/StepIndicator';

const OTP_LENGTH = 6;

const RegisterStep3Screen = ({navigation}) => {
  const {t} = useTranslation();
  const {ownerName, email, phone, countryCode} = useRegistrationStore();
  const register = useAuthStore(state => state.register);
  const [digits, setDigits] = useState(Array(OTP_LENGTH).fill(''));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [resendTimer, setResendTimer] = useState(60);
  const inputRefs = useRef([]);

  useEffect(() => {
    if (resendTimer <= 0) return;
    const interval = setInterval(() => {
      setResendTimer(prev => prev - 1);
    }, 1000);
    return () => clearInterval(interval);
  }, [resendTimer]);

  // Phone and countryCode sent separately to backend

  const handleChange = (text, index) => {
    const newDigits = [...digits];
    // Handle paste of full OTP
    if (text.length > 1) {
      const chars = text.replace(/\D/g, '').slice(0, OTP_LENGTH).split('');
      chars.forEach((ch, i) => {
        if (i < OTP_LENGTH) newDigits[i] = ch;
      });
      setDigits(newDigits);
      const nextIndex = Math.min(chars.length, OTP_LENGTH - 1);
      inputRefs.current[nextIndex]?.focus();
      if (chars.length === OTP_LENGTH) {
        submitOtp(newDigits.join(''));
      }
      return;
    }

    newDigits[index] = text.replace(/\D/g, '');
    setDigits(newDigits);

    if (text && index < OTP_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }

    // Auto-submit when all digits filled
    if (text && index === OTP_LENGTH - 1) {
      const otp = newDigits.join('');
      if (otp.length === OTP_LENGTH) {
        submitOtp(otp);
      }
    }
  };

  const handleKeyPress = (e, index) => {
    if (e.nativeEvent.key === 'Backspace' && !digits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const getErrorMessage = err => {
    const errorData = err.response?.data?.error;
    if (errorData?.details?.length > 0) {
      return errorData.details.map(d => `${d.field}: ${d.message}`).join(', ');
    }
    return errorData?.message || t('common.error');
  };

  const submitOtp = async otp => {
    setLoading(true);
    setError('');
    try {
      await register({
        ownerName,
        email,
        phone,
        otp,
        countryCode,
      });
      navigation.navigate('RegisterStep4');
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (resendTimer > 0) return;
    try {
      await authApi.requestOtp({countryCode, phone, purpose: 'REGISTRATION'});
      setResendTimer(60);
      setError('');
    } catch (err) {
      setError(err.response?.data?.error?.message || t('common.error'));
    }
  };

  return (
    <KeyboardAvoidingView
      style={styles.container}
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}>
      <ScrollView
        contentContainerStyle={styles.content}
        keyboardShouldPersistTaps="handled">
        <StepIndicator currentStep={3} />

        <Text style={styles.title}>{t('registration.otpTitle')}</Text>

        <View style={styles.otpRow}>
          {Array.from({length: OTP_LENGTH}, (_, index) => (
            <TextInput
              key={index}
              ref={ref => (inputRefs.current[index] = ref)}
              style={[
                styles.otpInput,
                digits[index] ? styles.otpInputFilled : null,
              ]}
              value={digits[index]}
              onChangeText={text => handleChange(text, index)}
              onKeyPress={e => handleKeyPress(e, index)}
              keyboardType="number-pad"
              maxLength={index === 0 ? OTP_LENGTH : 1}
              textContentType="oneTimeCode"
              autoComplete={index === 0 ? 'sms-otp' : 'off'}
            />
          ))}
        </View>

        {error ? <Text style={styles.errorText}>{error}</Text> : null}

        {loading && (
          <Text style={styles.loadingText}>{t('common.loading')}</Text>
        )}

        <TouchableOpacity
          style={styles.resendLink}
          onPress={handleResend}
          disabled={resendTimer > 0}>
          <Text
            style={[
              styles.resendText,
              resendTimer > 0 && styles.resendDisabled,
            ]}>
            {resendTimer > 0
              ? t('registration.resendIn', {seconds: resendTimer})
              : t('registration.haventReceivedCode')}
          </Text>
        </TouchableOpacity>

        <TouchableOpacity
          style={styles.changePhoneLink}
          onPress={() => navigation.goBack()}>
          <Text style={styles.changePhoneText}>
            {t('registration.correctPhoneNumber')}
          </Text>
        </TouchableOpacity>
      </ScrollView>
    </KeyboardAvoidingView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: onboardingColors.background,
  },
  content: {
    flexGrow: 1,
    paddingHorizontal: 32,
    paddingTop: 60,
  },
  title: {
    fontSize: 20,
    fontWeight: 'bold',
    color: onboardingColors.text,
    textAlign: 'center',
    marginTop: 16,
    marginBottom: 32,
    lineHeight: 28,
  },
  otpRow: {
    flexDirection: 'row',
    justifyContent: 'center',
    marginBottom: 24,
  },
  otpInput: {
    width: 44,
    height: 52,
    borderWidth: 1.5,
    borderColor: onboardingColors.dot,
    borderRadius: 8,
    marginHorizontal: 4,
    textAlign: 'center',
    fontSize: 22,
    fontWeight: '600',
    color: onboardingColors.text,
    backgroundColor: onboardingColors.white,
  },
  otpInputFilled: {
    borderColor: onboardingColors.primary,
  },
  errorText: {
    color: '#D32F2F',
    fontSize: 13,
    textAlign: 'center',
    marginBottom: 16,
  },
  loadingText: {
    color: onboardingColors.textSecondary,
    fontSize: 14,
    textAlign: 'center',
    marginBottom: 16,
  },
  resendLink: {
    alignItems: 'center',
    marginTop: 16,
  },
  resendText: {
    fontSize: 14,
    color: onboardingColors.primary,
    fontWeight: '500',
  },
  resendDisabled: {
    color: onboardingColors.textSecondary,
  },
  changePhoneLink: {
    alignItems: 'center',
    marginTop: 16,
  },
  changePhoneText: {
    fontSize: 14,
    color: onboardingColors.primary,
    fontWeight: '500',
  },
});

export default RegisterStep3Screen;
